import {
  AlignmentPolymerType,
  AlignmentSummary,
  ProteinAlignment,
  UNKNOWN_VALUE_BASES,
} from 'constants/AlignmentConstants';
import {AaSequenceAlignmentSummary} from 'molbio/aa-sequences/util/AaSequenceConstants';
import {ResidueHighlightFormat} from 'molbio/AlignmentPDFHelpers/AlignmentPDFConstants';

import {AaSequenceAlignmentSummaryGraphQL} from '../../graphql-fragments/molbio/__generated/AaSequenceAlignmentSummaryGraphQL';

/**
 * Returns if and how a residue should be highlighted based on mismatches vs unknown residues
 * as compared to the residue on the master sequence at the same index.
 */
export function getResidueHighlight(
  masterResidues: string,
  nonMasterResidues: string,
  index: number,
  alignmentPolymerType: AlignmentPolymerType = AlignmentPolymerType.DNA
): ResidueHighlightFormat | null {
  const residueBoundaries = getResidueRange(nonMasterResidues);
  if (!residueBoundaries || index < residueBoundaries.start || index > residueBoundaries.endInclusive) {
    // All gaps or index is on an edge gap, so we don't highlight
    return null;
  }
  const masterResidue = masterResidues[index].toLowerCase();
  const nonMasterResidue = nonMasterResidues[index].toLowerCase();

  if (
    nonMasterResidue === masterResidue ||
    (alignmentPolymerType === AlignmentPolymerType.DNA &&
      ['u', 't'].includes(nonMasterResidue) &&
      ['u', 't'].includes(masterResidue))
  ) {
    return null;
  }

  if (nonMasterResidue === UNKNOWN_VALUE_BASES[alignmentPolymerType]) {
    return ResidueHighlightFormat.UNKNOWN;
  }

  return ResidueHighlightFormat.MUTATION;
}

/**
 * Get the index of the first non-gap residue in the sequence.
 *
 * @package
 */
export function firstNonGapResidue(residues: string): number | null {
  for (let i = 0; i < residues.length; i++) {
    if (residues[i] !== '-') {
      return i;
    }
  }
  return null;
}

/**
 * Get the index of the last non-gap residue in the sequence.
 *
 * @package
 */
export function lastNonGapResidueIndex(residues: string): number | null {
  for (let i = residues.length - 1; i >= 0; i--) {
    if (residues[i] !== '-') {
      return i;
    }
  }
  return null;
}

/**
 * Get the first and last indices of actual residues, not gaps, in the sequence.
 */
function getResidueRange(residues: string): {start: number; endInclusive: number} | null {
  const start = firstNonGapResidue(residues);
  const endInclusive = lastNonGapResidueIndex(residues);
  if (start == null || endInclusive == null) {
    return null;
  }
  return {start, endInclusive};
}

/**
 * Whether the given alignment summary refers to a protein alignment, as opposed to a nucleotide alignment
 */
export function isAaSequenceAlignment(data: AlignmentSummary): data is AaSequenceAlignmentSummaryGraphQL {
  return data.hasOwnProperty('protein');
}

/**
 * Whether the given alignment state is a full protein alignment dict, as opposed to a summary dict
 */
export function isFullAaSequenceAlignment(
  alignment: ProteinAlignment | AaSequenceAlignmentSummary
): alignment is ProteinAlignment {
  return alignment.hasOwnProperty('rows');
}
